import * as z from "zod";
import { branchVolumeSeedSchema } from "../../shared/mutators/seedBranchVolumes";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { CollapsibleNotice } from "../components/Chat/SystemMessages";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { useToolOutput } from "../hooks/useToolOutput";

type Source = z.infer<typeof branchVolumeSeedSchema>;
type State = "pending" | "interrupted" | "done" | "kept" | "failed" | "settled";

const inputSchema = z.object({
  from: branchVolumeSeedSchema,
  reason: z.string().optional(),
});

const outputSchema = z.object({
  copied: z.array(z.string()),
  keptExisting: z.array(z.string()),
});

const errorSchema = z.object({ error: z.string() });

export function parseSeedBranchStorageInput(
  input: unknown
): { from: Source; reason: string | null } | null {
  const parsed = inputSchema.safeParse(input);
  if (!parsed.success) return null;
  const reason = parsed.data.reason?.trim();
  return { from: parsed.data.from, reason: reason ? reason : null };
}

export function seedBranchStorageState({
  completed,
  isError,
  interrupted,
  output,
}: {
  completed: boolean;
  isError: boolean;
  interrupted: boolean;
  output: unknown;
}): State {
  if (!completed) return interrupted ? "interrupted" : "pending";
  if (isError) return "failed";
  const parsed = outputSchema.safeParse(output);
  if (!parsed.success) return "settled";
  return parsed.data.copied.length === 0 && parsed.data.keptExisting.length > 0
    ? "kept"
    : "done";
}

function seedBranchStorageLabel(from: Source, state: State): string {
  if (from === "empty") {
    switch (state) {
      case "pending":
        return "Starting with empty storage";
      case "interrupted":
        return "Stopped setting up storage";
      case "failed":
        return "Couldn’t set up storage";
      case "done":
      case "kept":
      case "settled":
        return "Started with empty storage";
    }
  }
  const source = from === "live" ? "live" : "the source branch";
  switch (state) {
    case "pending":
      return `Copying storage from ${source}`;
    case "interrupted":
      return `Stopped copying storage from ${source}`;
    case "failed":
      return `Couldn’t copy storage from ${source}`;
    case "kept":
      return "This branch already has its own storage, so nothing was copied";
    case "done":
      return `Copied storage from ${source}`;
    case "settled":
      return `Set up storage from ${source}`;
  }
}

function SeedBranchStorageHeader({
  toolUse,
  result,
  interrupted,
}: ToolUseProps) {
  const state = seedBranchStorageState({
    completed: result !== null,
    isError: result?.isError ?? false,
    interrupted,
    output: result?.output,
  });
  const parsed = parseSeedBranchStorageInput(toolUse.input);
  return (
    <ToolStatus
      icon="box"
      text={
        parsed
          ? seedBranchStorageLabel(parsed.from, state)
          : "Setting up storage"
      }
      pending={state === "pending"}
    />
  );
}

export const seedBranchStorage: ChatTool = {
  name: "seedBranchStorage",
  Header: SeedBranchStorageHeader,
  alwaysLoadOutput: true,
};

export function SeedBranchStorageNotice({
  entryId,
  input,
  completed,
  isError,
  interrupted,
}: {
  entryId: string;
  input: unknown;
  completed: boolean;
  isError: boolean;
  interrupted: boolean;
}) {
  const { output } = useToolOutput(entryId, completed);
  const parsed = parseSeedBranchStorageInput(input);
  if (!parsed) return null;
  const state = seedBranchStorageState({
    completed,
    isError,
    interrupted,
    output,
  });
  const detail =
    state === "failed"
      ? errorSchema.safeParse(output).data?.error
      : parsed.reason;
  return (
    <div role="status" style={{ maxWidth: "100%" }}>
      <CollapsibleNotice
        icon="box"
        summary={seedBranchStorageLabel(parsed.from, state)}
        details={detail}
      />
    </div>
  );
}
